import React, { useContext } from "react";

import styled from "@emotion/styled";

import useAddressApi from "../../../../../../hooks/useAddressApi";
import { DataContext } from "../../../../../../providers/PelletsCheckoutDataProvider";
import AddressFallback from "../../../../../shared/AddressFallback";
import FormError from "../../../../../shared/forms/FormError";
import Input from "../../../../../shared/forms/NewInput";
import SelectInput, {
    SalutationSelect,
} from "../../../../../shared/forms/NewSelect";
import Headline from "../../../../../shared/NewHeadline";

const AddressInputWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const CityRow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    ${({ theme }) => theme.breakpoints.from.small.css`
       grid-template-columns: 1fr 5fr;
       gap: 10px;
    `}
`;

const StreetRow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    ${({ theme }) => theme.breakpoints.from.small.css`
       grid-template-columns: 5fr 1fr;
       gap: 10px;
    `}
`;
const InvoiceAddress = () => {
    const dataContext = useContext(DataContext);

    const { invoiceAddress } = dataContext.data;
    const {
        setInvoiceAddress,
        addFormError,
        clearFormError,
        hasFormErrorAt,
        onGooglePlacesChange,
    } = dataContext.actions;

    const { streets, cities, addressApiError } = useAddressApi({
        zipcode: invoiceAddress.zipcode,
        city: invoiceAddress.city,
    });

    const handleChange = (event) => {
        setInvoiceAddress({
            ...invoiceAddress,
            ...{ [event.target.name]: event.target.value },
        });
    };

    const handleCityChange = (event) => {
        if (event.target.value !== "") {
            clearFormError("inv_city");
        }
        setInvoiceAddress({
            ...invoiceAddress,
            ...{ city: event.target.value, street: "" },
        });
    };

    const handleStreetChange = (event) => {
        if (event.target.value !== "") {
            clearFormError("inv_street");
        }
        setInvoiceAddress({
            ...invoiceAddress,
            ...{ street: event.target.value ?? "" },
        });
    };

    const handleZipcodeChange = (event) => {
        if (event.target.value !== "") {
            clearFormError("inv_zipcode");
        }
        setInvoiceAddress({
            ...invoiceAddress,
            ...{ zipcode: event.target.value, street: "", city: "" },
        });
    };

    const handleHouseNumberChange = (event) => {
        if (event.target.value !== "") {
            clearFormError("inv_house_number");
        }
        setInvoiceAddress({
            ...invoiceAddress,
            house_number: event.target.value,
        });
    };

    return (
        <React.Fragment>
            <Headline>Rechnungsadresse</Headline>
            <div className="field">
                <div className="control">
                    <Input
                        placeholder="Firma (optional)"
                        id="inv_company"
                        name="company"
                        onChange={handleChange}
                        value={invoiceAddress.company}
                        validation="company"
                        forceError={hasFormErrorAt("inv_company")}
                        onError={() => addFormError("inv_company")}
                        onErrorSolved={() => clearFormError("inv_company")}
                    />
                </div>
            </div>

            <div className="field">
                <div className="control">
                    <SalutationSelect
                        name="salutation"
                        placeholder="Anrede"
                        onChange={handleChange}
                        value={invoiceAddress.salutation}
                    />
                </div>
            </div>

            <Input
                placeholder="Vorname"
                id="inv_firstname"
                name="firstname"
                onChange={handleChange}
                value={invoiceAddress.firstname}
                validation="name"
                fullWidth
                forceError={hasFormErrorAt("inv_firstname")}
                onError={() => addFormError("inv_firstname")}
                onErrorSolved={() => clearFormError("inv_firstname")}
            />

            <Input
                placeholder="Nachname"
                id="inv_lastname"
                name="lastname"
                onChange={handleChange}
                value={invoiceAddress.lastname}
                validation="name"
                forceError={hasFormErrorAt("inv_lastname")}
                onError={() => addFormError("inv_lastname")}
                onErrorSolved={() => clearFormError("inv_lastname")}
            />
            {!addressApiError ? (
                <AddressInputWrapper>
                    <CityRow>
                        <Input
                            placeholder="Postleitzahl"
                            id="inv_zipcode"
                            name="zipcode"
                            onChange={handleZipcodeChange}
                            value={invoiceAddress.zipcode}
                            validation="zipcode"
                            fullWidth
                            forceError={hasFormErrorAt("inv_zipcode")}
                            onError={() => addFormError("inv_zipcode")}
                            onErrorSolved={() => clearFormError("inv_zipcode")}
                            withField
                        />
                        <div>
                            <SelectInput
                                name="city"
                                placeholder="Bitte wähle Deinen Ort aus"
                                withDefault
                                options={cities.map((city) => ({
                                    value: city.ONAME,
                                    text: city.ONAME,
                                }))}
                                value={
                                    cities.find((city) => {
                                        return (
                                            city.ONAME === invoiceAddress.city
                                        );
                                    })?.ONAME ?? ""
                                }
                                onChange={handleCityChange}
                            />
                            {hasFormErrorAt("inv_city") && (
                                <FormError
                                    message={"Bitte wähle einen Ort aus."}
                                />
                            )}
                        </div>
                    </CityRow>

                    <StreetRow>
                        <div>
                            <SelectInput
                                name="street"
                                placeholder="Bitte wähle Deine Straße aus"
                                withDefault
                                options={streets.map((street) => {
                                    return {
                                        value: street,
                                        text: street,
                                    };
                                })}
                                value={
                                    streets.find(
                                        (street) =>
                                            street === invoiceAddress.street
                                    ) ?? ""
                                }
                                onChange={handleStreetChange}
                            />
                            {hasFormErrorAt("inv_street") && (
                                <FormError
                                    message={"Bitte wähle eine Straße aus."}
                                />
                            )}
                        </div>

                        <Input
                            placeholder="Haus.-Nr."
                            id="house_number"
                            name="house_number"
                            onChange={handleChange}
                            value={invoiceAddress.house_number}
                            validation="house_number"
                            forceError={hasFormErrorAt("inv_house_number")}
                            onError={() => addFormError("inv_house_number")}
                            onErrorSolved={() =>
                                clearFormError("inv_house_number")
                            }
                        />
                    </StreetRow>
                </AddressInputWrapper>
            ) : (
                <AddressFallback
                    onGooglePlacesChange={(placesData) =>
                        onGooglePlacesChange(placesData, true)
                    }
                    zipcode={invoiceAddress.zipcode}
                    handleHouseNumberChange={handleHouseNumberChange}
                    houseNumber={invoiceAddress.house_number}
                    houseNumberHasErrorAt={hasFormErrorAt(
                        "invoiceAddress.house_number"
                    )}
                    houseNumberAddError={() =>
                        addFormError("invoiceAddress.house_number")
                    }
                    houseNumberClearError={() =>
                        clearFormError("invoiceAddress.house_number")
                    }
                />
            )}
        </React.Fragment>
    );
};

export default InvoiceAddress;
